import { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { LayoutGrid, Image, MessageCircle, Zap, Loader, WifiOff, UserCircle, Mail, ClipboardList, Sun, Moon } from 'lucide-react';
import { useParticipantPolling } from './useParticipantPolling';
import { useParticipantTheme } from './useParticipantTheme';
import { SessionTab } from './tabs/SessionTab';
import { AssetsTab } from './tabs/AssetsTab';
import { ChatTab } from './tabs/ChatTab';
import { ActivityTab } from './tabs/ActivityTab';
import { BriefTab } from './tabs/BriefTab';
import { InboxTab } from './tabs/InboxTab';
import { DecisionsTab } from './tabs/DecisionsTab';

type Tab = 'session' | 'assets' | 'chat' | 'activity' | 'brief' | 'inbox' | 'decisions';

const TABS: { id: Tab; label: string; Icon: React.FC<any> }[] = [
  { id: 'session', label: 'Session', Icon: LayoutGrid },
  { id: 'assets', label: 'Assets', Icon: Image },
  { id: 'chat', label: 'Chat', Icon: MessageCircle },
  { id: 'activity', label: 'Activity', Icon: Zap },
];

// Tabletop-only tabs (plan 6.3) — appended when state.tabletop is present,
// rather than mixed into TABS unconditionally, so standard sessions keep
// exactly the 4 tabs they had before Phase 4.
const TABLETOP_TABS: { id: Tab; label: string; Icon: React.FC<any> }[] = [
  { id: 'brief', label: 'Brief', Icon: UserCircle },
  { id: 'inbox', label: 'Inbox', Icon: Mail },
  { id: 'decisions', label: 'Decisions', Icon: ClipboardList },
];

export default function ParticipantView() {
  useParams<{ token: string }>();
  const location = useLocation();
  const sessionId = location.state?.sessionId;
  const [activeTab, setActiveTab] = useState<Tab>('session');
  const [theme, setTheme] = useParticipantTheme();

  const { state, error, loading, refresh } = useParticipantPolling(sessionId ?? null);

  if (!sessionId) {
    return (
      <div data-participant-theme={theme} className="fixed inset-0 bg-[var(--pt-bg)] flex items-center justify-center p-6">
        <p className="text-[var(--pt-text-dim)] text-sm">Invalid session — please scan your QR code again.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div data-participant-theme={theme} className="fixed inset-0 bg-[var(--pt-bg)] flex items-center justify-center">
        <Loader size={28} className="text-primary animate-spin" />
      </div>
    );
  }

  if (error && !state) {
    return (
      <div data-participant-theme={theme} className="fixed inset-0 bg-[var(--pt-bg)] flex flex-col items-center justify-center p-6 gap-4">
        <WifiOff size={32} className="text-[var(--pt-text-dimmer)]" />
        <p className="text-[var(--pt-text-dim)] text-sm text-center">Connection lost — {error}</p>
        <button onClick={refresh} className="text-primary text-sm font-medium">Retry</button>
      </div>
    );
  }

  if (!state) return null;

  const hasActiveWidget = !!state.activeWidget;
  const tabletop = state.tabletop;
  const visibleTabs = tabletop ? [...TABS, ...TABLETOP_TABS] : TABS;

  const tabContent = (
    <>
      {activeTab === 'session' && <SessionTab state={state} sessionId={sessionId} />}
      {activeTab === 'assets' && <AssetsTab assets={state.publishedAssets} />}
      {activeTab === 'chat' && <ChatTab sessionId={sessionId} state={state} />}
      {activeTab === 'activity' && (
        <ActivityTab
          sessionId={sessionId}
          widget={state.activeWidget}
          onSubmit={refresh}
        />
      )}
      {activeTab === 'brief' && tabletop && (
        <BriefTab sessionId={sessionId} tabletop={tabletop} onSeatClaimed={refresh} />
      )}
      {activeTab === 'inbox' && tabletop && <InboxTab tabletop={tabletop} />}
      {activeTab === 'decisions' && tabletop && (
        <DecisionsTab sessionId={sessionId} tabletop={tabletop} onSubmit={refresh} />
      )}
    </>
  );

  // Below `lg`: unchanged phone layout (header, single-column content, bottom
  // tab bar). At `lg` and up: a left sidebar nav replaces the bottom bar, and
  // content gets a real desktop-width column instead of staying pinned to a
  // phone-width `max-w-lg` — this is what was rendering as a narrow strip of
  // content in a sea of black on a desktop browser tab.
  return (
    <div data-participant-theme={theme} className="fixed inset-0 bg-[var(--pt-bg)] overflow-hidden flex">

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:flex-shrink-0 border-r border-[var(--pt-border)] h-full">
        <div className="px-5 py-6 border-b border-[var(--pt-border)] flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-[var(--pt-text)] font-semibold text-lg leading-tight truncate">{state.sessionName}</h1>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${error ? 'bg-yellow-400' : 'bg-primary animate-pulse'}`} />
              <span className="text-[var(--pt-text-dimmer)] text-xs">{error ? 'Offline' : 'Live'}</span>
            </div>
          </div>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-[var(--pt-text-dim)] hover:bg-[var(--pt-surface)] hover:text-[var(--pt-text)] transition-colors"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
        <nav className="flex-1 flex flex-col gap-1 p-3 overflow-y-auto">
          {visibleTabs.map(({ id, label, Icon }) => {
            const isActive = activeTab === id;
            const showBadge = id === 'activity' && hasActiveWidget;
            return (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left ${
                  isActive ? 'bg-primary/15 text-primary' : 'text-[var(--pt-text-dim)] hover:bg-[var(--pt-surface)] hover:text-[var(--pt-text)]'
                }`}
              >
                <div className="relative flex-shrink-0">
                  <Icon size={18} />
                  {showBadge && <span className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-primary rounded-full" />}
                </div>
                {label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0 h-full">
        <div className="h-full w-full max-w-lg lg:max-w-none mx-auto flex flex-col">

          {/* Mobile-only header */}
          <div className="lg:hidden flex-shrink-0 px-4 pt-safe pb-3 border-b border-[var(--pt-border)]" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-[var(--pt-text)] font-semibold text-base leading-tight truncate max-w-56">{state.sessionName}</h1>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="text-[var(--pt-text-dimmer)] text-xs">Live</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {error && (
                  <span className="text-xs text-yellow-400/70 bg-yellow-400/10 px-2 py-1 rounded-full">Offline</span>
                )}
                <button
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  className="flex items-center justify-center w-8 h-8 rounded-lg text-[var(--pt-text-dim)] hover:bg-[var(--pt-surface)]"
                >
                  {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                </button>
              </div>
            </div>
          </div>

          {/* Tab content — phone-width on mobile; a real desktop column above
              `lg` that widens further at `xl`/`2xl` so it doesn't leave a
              huge dead void on wide monitors while still capping line length
              for readability. */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            <div className="flex-1 flex flex-col overflow-hidden min-h-0 lg:max-w-3xl xl:max-w-5xl 2xl:max-w-6xl lg:w-full lg:mx-auto lg:px-6 lg:py-6">
              {tabContent}
            </div>
          </div>

          {/* Mobile-only bottom tab bar */}
          <div className="lg:hidden flex-shrink-0 bg-[var(--pt-bg)] border-t border-[var(--pt-border)] overflow-x-auto" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.25rem)' }}>
            <div className="flex min-w-max">
              {visibleTabs.map(({ id, label, Icon }) => {
                const isActive = activeTab === id;
                const showBadge = id === 'activity' && hasActiveWidget;
                return (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`flex-1 min-w-[64px] flex flex-col items-center gap-1 py-3 transition-colors relative ${
                      isActive ? 'text-primary' : 'text-[var(--pt-text-dimmer)]'
                    }`}
                  >
                    <div className="relative">
                      <Icon size={20} />
                      {showBadge && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full" />
                      )}
                    </div>
                    <span className="text-xs font-medium">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
